/**
 * Labstep
 *
 * @module components/Folder/Action/UpdateOuter/Bulk
 * @desc Bulk action to update outer folder for multiple folders
 */

import { useHasAccessMultiple } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { ICONS } from 'labstep-web/constants/icons';
import { withActiveGroup } from 'labstep-web/containers/ActiveGroup';
import type { WithActiveGroupInjectedProps } from 'labstep-web/containers/ActiveGroup/types';
import { EntityCanUpdateContainer } from 'labstep-web/containers/Entity/Update/Can';
import { useBulkSelectContext } from 'labstep-web/contexts/bulk-select/hook';
import { ActionComponentBulk } from 'labstep-web/core/Action/Component/Bulk';
import { ModalDefaultContainer as ModalDefault } from 'labstep-web/core/Modal/Default';
import { SearchSelectContainer as SearchSelect } from 'labstep-web/core/Select/Search';
import type { APICallOptions } from 'labstep-web/models';
import type { Entity } from 'labstep-web/models/entity.model';
import { Folder } from 'labstep-web/models/folder.model';
import type React from 'react';

interface FolderActionUpdateOuterBulkProps
  extends WithActiveGroupInjectedProps {
  folders: Folder[];
  options?: APICallOptions;
}

export const FolderActionUpdateOuterBulk: React.FC<
  FolderActionUpdateOuterBulkProps
> = ({ folders, activeGroup, options }) => {
  const { unselectAll } = useBulkSelectContext();

  const folderIdsAccessEditObject = useHasAccessMultiple(
    Folder.entityName,
    folders.map((folder: Folder) => folder.id),
    Action.edit,
  );

  if (folders.length === 0) {
    return null;
  }

  const foldersWithEditPermission = folders.filter(
    (folder: Folder) => true, //folderIdsAccessEditObject[folder.id],
  );

  if (foldersWithEditPermission.length === 0) {
    return null;
  }

  // Get all inner folder IDs to exclude from selection
  const allInnerFolderIds = folders.reduce(
    (acc: number[], folder: Folder) => {
      return [...acc, ...folder.inner_folder_ids];
    },
    [],
  );

  // Determine folder type from first folder (should be consistent)
  const folderType = folders[0]?.type;

  const handleSuccess = (response?: any) => {
    unselectAll();
    options?.onSuccess?.(response);
  };

  return (
    <EntityCanUpdateContainer
      entityName={Folder.entityName}
      id={foldersWithEditPermission.map((folder) => folder.id)}
      optimistic
    >
      {({ status, update }) => (
        <ModalDefault
          data-cy="components/Folder/Action/UpdateOuter/Bulk:modal"
          header={`Move ${foldersWithEditPermission.length} ${Folder.getHumanReadableEntityName(
            foldersWithEditPermission.length > 1,
            true,
          )} to`}
          content={({ toggleModal }) => (
            <SearchSelect
              autoFocus
              openMenuOnFocus
              entityName={Folder.entityName}
              params={{
                type: folderType,
                group_id: activeGroup?.id,
                not_id: allInnerFolderIds,
                count: 50,
              }}
              labelKey="folder_path"
              onChange={(value: Entity | null) => {
                const body = {
                  outer_folder_id: value ? value.id : null,
                };
                update(body, {
                  ...options,
                  onSuccess: (response: any) => {
                    toggleModal();
                    handleSuccess(response);
                  },
                });
              }}
            />
          )}
          viewComponent={({ toggleModal }) => (
            <ActionComponentBulk
              onClick={toggleModal}
              icon={ICONS.folder.primary}
              content={`Move to ${Folder.getHumanReadableEntityName(false, true)}`}
              entitiesSelectedCount={folders.length}
              entitiesWithPermissionCount={
                foldersWithEditPermission.length
              }
              dataCy="components/Folder/Action/UpdateOuter/Bulk"
            />
          )}
        />
      )}
    </EntityCanUpdateContainer>
  );
};

export default withActiveGroup(FolderActionUpdateOuterBulk);
